import { FC, useEffect, useRef, useState } from 'react'

import { Button, ButtonSizes, ButtonVariants } from '@inno-ui/button'
import {
  ArrowCirclePathLeftIcon,
  BugLineIcon,
  CloudArrowDownLineIcon,
  ExclamationCircleLineIcon,
  ExclamationTriangleLineIcon,
  FunnelLineIcon,
  IconSizes,
  InformationCircleLineIcon,
  MultiplyCircleLineIcon,
  PlusCircleLineIcon,
  SlidersLineIcon,
  SortDownIcon,
  SortUpIcon,
  TrashLineIcon
} from '@inno-ui/icons'
import cn from 'classnames'
import { useTranslation } from 'react-i18next'
import type {
  LogEntry,
  LogExport,
  LogLevel
} from 'shared/services/logger/logger.types'

import type { LogFilterState } from '../hooks/useLogFilter'
import type {
  ConditionField,
  ConditionOperator,
  FilterCondition
} from '../logViewer.types'
import {
  EVENT_TYPES,
  LOG_LEVELS,
  NO_VALUE_OPS,
  OPERATORS_FOR_FIELD
} from '../logViewer.types'
import styles from './LogToolbar.module.scss'

// ─── Level icon map ────────────────────────────────────────────────────────────

const LEVEL_ICON: Record<LogLevel, FC<{ size?: IconSizes; color?: string }>> = {
  debug: BugLineIcon,
  info: InformationCircleLineIcon,
  warn: ExclamationTriangleLineIcon,
  error: ExclamationCircleLineIcon
}

const LEVEL_CHIP_CLASS: Record<LogLevel, string> = {
  debug: styles.chipDebug,
  info: styles.chipInfo,
  warn: styles.chipWarn,
  error: styles.chipError
}

// ─── Props ─────────────────────────────────────────────────────────────────────

interface LogToolbarProps extends LogFilterState {
  mode: 'live' | 'file'
  fileData: LogExport | null
  activeEntries: LogEntry[]
  onRefresh: () => void
  onExport: () => void
  onClearLogs: () => void
  onLoadAnother: () => void
}

// ─── Component ────────────────────────────────────────────────────────────────

export const LogToolbar: FC<LogToolbarProps> = ({
  mode,
  fileData,
  activeEntries,
  levelFilter,
  setLevelFilter,
  typeFilter,
  setTypeFilter,
  sortOrder,
  toggleSortOrder,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  onlyWithPayload,
  toggleOnlyWithPayload,
  showAdvanced,
  toggleShowAdvanced,
  conditions,
  filterLogic,
  setFilterLogic,
  activeConditions,
  isAnyFilterActive,
  levelCounts,
  typeCounts,
  addCondition,
  removeCondition,
  updateCondition,
  clearAll,
  setDateRangeFromEntries,
  onRefresh,
  onExport,
  onClearLogs,
  onLoadAnother
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'desktop.log_viewer'
  })

  const { t: tToolbar } = useTranslation('translation', {
    keyPrefix: 'desktop.log_viewer.toolbar'
  })

  return (
    <div className={styles.toolbar}>
      {/* Row 1: sort + actions */}
      <div className={styles.toolbarRow}>
        <div className={styles.toolbarLeft}>
          <button
            className={cn(styles.iconBtn, {
              [styles.iconBtnActive]: sortOrder === 'asc'
            })}
            onClick={toggleSortOrder}
            title={
              sortOrder === 'desc'
                ? tToolbar('sort_newest')
                : tToolbar('sort_oldest')
            }
          >
            {sortOrder === 'desc' ? (
              <SortDownIcon size={IconSizes.Size16} />
            ) : (
              <SortUpIcon size={IconSizes.Size16} />
            )}
          </button>

          <button
            className={cn(styles.iconBtn, {
              [styles.iconBtnActive]: showAdvanced
            })}
            onClick={toggleShowAdvanced}
            title={tToolbar('time_range')}
          >
            <SlidersLineIcon size={IconSizes.Size16} />
          </button>

          {isAnyFilterActive && (
            <button
              className={cn(styles.iconBtn, styles.iconBtnClear)}
              onClick={clearAll}
              title={tToolbar('clear_filters')}
            >
              <MultiplyCircleLineIcon size={IconSizes.Size16} />
              <span className={styles.clearLabel}>
                {tToolbar('clear_filters')}
              </span>
            </button>
          )}
        </div>

        <div className={styles.toolbarRight}>
          {mode === 'live' && (
            <>
              <Button
                variant={ButtonVariants.Ghost}
                size={ButtonSizes.S}
                icon={ArrowCirclePathLeftIcon}
                onClick={onRefresh}
                label={tToolbar('refresh')}
              />
              <Button
                variant={ButtonVariants.Ghost}
                size={ButtonSizes.S}
                icon={CloudArrowDownLineIcon}
                onClick={onExport}
                label={tToolbar('export')}
              />
              <Button
                variant={ButtonVariants.Ghost}
                size={ButtonSizes.S}
                icon={TrashLineIcon}
                onClick={onClearLogs}
                label={tToolbar('clear')}
              />
            </>
          )}
          {mode === 'file' && fileData && (
            <Button
              variant={ButtonVariants.Ghost}
              size={ButtonSizes.S}
              onClick={onLoadAnother}
              label={tToolbar('load_another')}
            />
          )}
        </div>
      </div>

      {/* Row 2: level chips */}
      <div className={styles.filterRow}>
        <span className={styles.filterLabel}>
          <FunnelLineIcon size={IconSizes.Size12} color='var(--grey-600)' />
          {tToolbar('level')}
        </span>
        <div className={styles.filterGroup}>
          <button
            className={cn(styles.filterChip, {
              [styles.filterChipActive]: levelFilter === 'all'
            })}
            onClick={() => setLevelFilter('all')}
          >
            {tToolbar('all')}
            <span className={styles.chipCount}>{activeEntries.length}</span>
          </button>
          {LOG_LEVELS.map(lvl => {
            const LvlIcon = LEVEL_ICON[lvl]
            return (
              <button
                key={lvl}
                className={cn(styles.filterChip, LEVEL_CHIP_CLASS[lvl], {
                  [styles.filterChipActive]: levelFilter === lvl
                })}
                onClick={() =>
                  setLevelFilter(levelFilter === lvl ? 'all' : lvl)
                }
              >
                <LvlIcon size={IconSizes.Size12} />
                {lvl}
                {(levelCounts[lvl] ?? 0) > 0 && (
                  <span className={styles.chipCount}>{levelCounts[lvl]}</span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Row 3: type chips */}
      <div className={styles.filterRow}>
        <span className={styles.filterLabel}>
          <FunnelLineIcon size={IconSizes.Size12} color='var(--grey-600)' />
          {tToolbar('type')}
        </span>
        <div className={styles.filterGroup}>
          <button
            className={cn(styles.filterChip, {
              [styles.filterChipActive]: typeFilter === 'all'
            })}
            onClick={() => setTypeFilter('all')}
          >
            {tToolbar('all_types')}
          </button>
          {EVENT_TYPES.map(type => (
            <button
              key={type}
              className={cn(styles.filterChip, {
                [styles.filterChipActive]: typeFilter === type
              })}
              onClick={() => setTypeFilter(typeFilter === type ? 'all' : type)}
            >
              {type}
              {(typeCounts[type] ?? 0) > 0 && (
                <span className={styles.chipCount}>{typeCounts[type]}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Row 4: query builder */}
      <div className={styles.queryBuilder}>
        <div className={styles.queryHeader}>
          <div className={styles.logicToggle}>
            <button
              className={cn(styles.logicBtn, {
                [styles.logicBtnActive]: filterLogic === 'and'
              })}
              onClick={() => setFilterLogic('and')}
            >
              AND
            </button>
            <button
              className={cn(styles.logicBtn, {
                [styles.logicBtnActive]: filterLogic === 'or'
              })}
              onClick={() => setFilterLogic('or')}
            >
              OR
            </button>
          </div>

          {conditions.length > 0 && (
            <span className={styles.conditionCount}>
              {t('query_builder.active_conditions', {
                count: activeConditions.length
              })}
            </span>
          )}

          <button className={styles.addConditionBtn} onClick={addCondition}>
            <PlusCircleLineIcon size={IconSizes.Size14} />
            {t('query_builder.add_condition')}
          </button>
        </div>

        {conditions.map((cond, idx) => (
          <ConditionRow
            key={cond.id}
            condition={cond}
            index={idx}
            logic={filterLogic}
            isLast={idx === conditions.length - 1}
            onChange={patch => updateCondition(cond.id, patch)}
            onRemove={() => removeCondition(cond.id)}
          />
        ))}
      </div>

      {/* Advanced: date range + extras */}
      {showAdvanced && (
        <div className={styles.advancedRow}>
          <div className={styles.dateRange}>
            <span className={styles.filterLabel}>{tToolbar('from')}</span>
            <input
              type='datetime-local'
              className={styles.dateInput}
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
            />
            <span className={styles.filterLabel}>{tToolbar('to')}</span>
            <input
              type='datetime-local'
              className={styles.dateInput}
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
            />
            {activeEntries.length > 0 && (
              <button
                className={styles.setRangeBtn}
                onClick={setDateRangeFromEntries}
              >
                {tToolbar('full_range')}
              </button>
            )}
          </div>
          <div className={styles.toggleGroup}>
            <button
              className={cn(styles.toggleChip, {
                [styles.toggleChipActive]: onlyWithPayload
              })}
              onClick={toggleOnlyWithPayload}
            >
              {tToolbar('has_payload')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── ConditionRow ──────────────────────────────────────────────────────────────

interface ConditionRowProps {
  condition: FilterCondition
  index: number
  logic: 'and' | 'or'
  isLast: boolean
  onChange: (patch: Partial<FilterCondition>) => void
  onRemove: () => void
}

const CONDITION_ROW_DEBOUNCE_MS = 300

const ConditionRow: FC<ConditionRowProps> = ({
  condition,
  index,
  logic,
  isLast,
  onChange,
  onRemove
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'desktop.log_viewer.query_builder'
  })

  // Local input state — inputs feel instant, parent state updates are debounced
  const [localValue, setLocalValue] = useState(condition.value)
  const [localPayloadKey, setLocalPayloadKey] = useState(condition.payloadKey)

  // Keep a stable ref to onChange so debounce effects never go stale
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  })

  // Sync from parent when condition is reset externally (e.g. clearAll)
  useEffect(() => {
    setLocalValue(condition.value)
  }, [condition.value])
  useEffect(() => {
    setLocalPayloadKey(condition.payloadKey)
  }, [condition.payloadKey])

  // Propagate value to parent after debounce
  useEffect(() => {
    if (localValue === condition.value) return
    const timer = setTimeout(
      () => onChangeRef.current({ value: localValue }),
      CONDITION_ROW_DEBOUNCE_MS
    )
    return () => clearTimeout(timer)
    // condition.value intentionally omitted — we only want this to fire when
    // the user types, not when the parent echoes the value back
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localValue])

  // Propagate payloadKey to parent after debounce
  useEffect(() => {
    if (localPayloadKey === condition.payloadKey) return
    const timer = setTimeout(
      () => onChangeRef.current({ payloadKey: localPayloadKey }),
      CONDITION_ROW_DEBOUNCE_MS
    )
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localPayloadKey])

  const validOps = OPERATORS_FOR_FIELD[condition.field]
  const showValue = !NO_VALUE_OPS.has(condition.operator)

  const FIELD_OPTIONS = [
    { value: 'event', labelKey: 'field_event' },
    { value: 'payload', labelKey: 'field_payload' },
    { value: 'payload_field', labelKey: 'field_payload_field' }
  ] as const

  const OP_LABELS: Record<ConditionOperator, string> = {
    contains: t('op_contains'),
    not_contains: t('op_not_contains'),
    equals: t('op_equals'),
    not_equals: t('op_not_equals'),
    starts_with: t('op_starts_with'),
    ends_with: t('op_ends_with'),
    exists: t('op_exists'),
    not_exists: t('op_not_exists')
  }

  return (
    <div className={styles.conditionRow}>
      <span className={styles.conditionIndex}>
        {index === 0 ? 'IF' : logic.toUpperCase()}
      </span>

      <select
        className={styles.conditionSelect}
        value={condition.field}
        onChange={e => onChange({ field: e.target.value as ConditionField })}
      >
        {FIELD_OPTIONS.map(f => (
          <option key={f.value} value={f.value}>
            {t(f.labelKey)}
          </option>
        ))}
      </select>

      {condition.field === 'payload_field' && (
        <input
          className={cn(styles.conditionInput, styles.conditionKey)}
          placeholder={t('placeholder_field')}
          value={localPayloadKey}
          onChange={e => setLocalPayloadKey(e.target.value)}
        />
      )}

      <select
        className={styles.conditionSelect}
        value={condition.operator}
        onChange={e =>
          onChange({ operator: e.target.value as ConditionOperator })
        }
      >
        {validOps.map(op => (
          <option key={op} value={op}>
            {OP_LABELS[op]}
          </option>
        ))}
      </select>

      {showValue && (
        <input
          className={cn(styles.conditionInput, styles.conditionValue)}
          placeholder={t('placeholder_value')}
          value={localValue}
          onChange={e => setLocalValue(e.target.value)}
        />
      )}

      <button className={styles.removeConditionBtn} onClick={onRemove}>
        <MultiplyCircleLineIcon
          size={IconSizes.Size14}
          color='var(--grey-600)'
        />
      </button>

      {!isLast && (
        <div className={styles.conditionConnector}>
          <span
            className={cn(styles.connectorLabel, {
              [styles.connectorOr]: logic === 'or'
            })}
          >
            {logic}
          </span>
        </div>
      )}
    </div>
  )
}
